import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styles from './PrivateToggle.module.css';

interface PrivateToggleProps {
  value: boolean;
  onChange: (next: boolean) => void;
  showHelper?: boolean;
  className?: string;
  /** Visual variant. 'standalone' (default) keeps the bordered pill chrome.
   *  'embedded' drops the outer .row border + padding + background so the
   *  toggle can sit inside a parent row container that owns those concerns
   *  (currently LeafActionSheet card 4). The internal label, ✦ star, info
   *  button + popover, track, and thumb stay intact — including the on-state
   *  italic label / opaque star / gold track / light thumb signals — per
   *  rule #15's "doubly protected" popover requirement. */
  variant?: 'standalone' | 'embedded';
}

/**
 * Compose/edit-time toggle for "Hold this one close" (the `isPrivate` flag).
 * Additive-privacy-safe: the label describes an action the user can take,
 * not a status that implies non-toggled prayers are exposed.
 *
 * The inline (i) button opens the same popover `PrivateBadge` uses so the
 * mental model is consistent across the app.
 */
export default function PrivateToggle({
  value,
  onChange,
  showHelper = false,
  className,
  variant = 'standalone',
}: PrivateToggleProps) {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    if (!infoOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setInfoOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [infoOpen]);

  return (
    <div className={[styles.wrapper, className ?? ''].filter(Boolean).join(' ')}>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        className={[
          styles.row,
          value ? styles.on : '',
          variant === 'embedded' ? styles.embedded : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => onChange(!value)}
      >
        <span className={styles.labelCluster}>
          <span className={styles.star} aria-hidden="true">✦</span>
          <span className={styles.label}>{t('private.toggleLabel')}</span>
        </span>
        <span
          className={styles.info}
          role="button"
          tabIndex={0}
          aria-label={t('private.toggleInfoAria')}
          onClick={(e) => {
            e.stopPropagation();
            setInfoOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              setInfoOpen(true);
            }
          }}
        >
          i
        </span>
        <span className={styles.track} aria-hidden="true">
          <span className={styles.thumb} />
        </span>
      </button>

      {showHelper && (
        <p className={styles.helper}>{t('private.toggleHelper')}</p>
      )}

      {infoOpen && <InfoPopover onClose={() => setInfoOpen(false)} />}
    </div>
  );
}

// Reuse PrivateBadge's popover styles via a local minimal import.
// This is intentionally inlined here (rather than a shared popover component)
// because it is only used in two places and a shared component would be
// dead weight. Styling is kept in sync by reference.
import badgeStyles from './PrivateBadge.module.css';

function InfoPopover({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  // Portal to body so the popover escapes ancestor stacking contexts.
  // PrayPage's `.privateToggleWrap` applies `transform: translateX(-50%)`,
  // which creates a stacking context and would otherwise trap the
  // backdrop's z-index beneath the Save button.
  return createPortal(
    <div
      className={badgeStyles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="private-toggle-popover-title"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className={badgeStyles.popover} onClick={(e) => e.stopPropagation()}>
        <h2 id="private-toggle-popover-title" className={badgeStyles.popoverTitle}>
          <span className={badgeStyles.popoverStar} aria-hidden="true">✦</span>
          {t('private.popoverTitle')}
        </h2>
        <p className={badgeStyles.popoverText}>{t('private.popoverText')}</p>
        <button
          type="button"
          className={badgeStyles.popoverClose}
          onClick={onClose}
          autoFocus
        >
          {t('private.popoverClose')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
